import { Injectable, inject } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { GenericResponseCode } from '@brightside-web/desktop/data-access/core-services';
import { Environment } from '@micro-core/environment';
import type { CoreAccountsInterface } from '@brightside-web/desktop/feature/financial-accounts-core';

import { DevMenuService } from '../dev-menu.service';

interface TransferCreateGetBody {
  result: { code: string };
  payload?: { accounts?: CoreAccountsInterface[] };
}

/**
 * In non-production, appends dev-menu mock external accounts to GET
 * `/accounts/transfer/create` when mocks are configured. POST is unchanged.
 */
@Injectable()
export class TransferFundsMockInterceptor implements HttpInterceptor {
  private readonly _env = inject(Environment);
  private readonly _devMenuService = inject(DevMenuService);

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (this._env.production) {
      return next.handle(req);
    }

    const mocks = this._devMenuService.mockExternalTransferAccounts();
    if (!mocks.length) {
      return next.handle(req);
    }

    if (req.method !== 'GET' || !req.url.includes('/accounts/transfer/create')) {
      return next.handle(req);
    }

    return next.handle(req).pipe(
      map((event) => {
        if (!(event instanceof HttpResponse)) {
          return event;
        }
        const body = event.body as TransferCreateGetBody | null;
        if (
          !body ||
          body.result?.code !== GenericResponseCode.SERVICE_OK ||
          !body.payload?.accounts
        ) {
          return event;
        }
        return event.clone({
          body: {
            ...body,
            payload: {
              ...body.payload,
              accounts: [...body.payload.accounts, ...mocks],
            },
          },
        });
      })
    );
  }
}
